<template>
  <div :style="{ width: width, 'max-width': maxWidth }">
    <slot name="label" v-if="$slots.label" />

    <div class="q-mb-sm" v-if="label && !$slots.label">
      <div>
        {{ label }}
        <span class="text-negative" v-if="required">*</span>
      </div>
    </div>

    <!-- Description -->
    <slot name="description" v-if="$slots.description" />
    <div class="text-secondary text-caption q-mb-xs" v-if="description">{{ description }}</div>

    <!-- Number -->
    <template v-if="type === 'number'">
      <q-input
        ref="input"
        type="number"
        v-model.number="currentValue"
        :placeholder="placeholder"
        :bg-color="bgColor"
        :disable="disabled"
        :prefix="prefix"
        :suffix="suffix"
        bottom-slots
        lazy-rules
        :rules="[...rules, validateRequired, validateMin, validateMax]"
        outlined
        dense
        hide-bottom-space
        @change="handleChange($event)"
      >
        <template v-slot:error>
          {{ $t(errorMsg) }}
        </template>
      </q-input>
    </template>

    <!-- Textarea -->
    <template v-else-if="type === 'textarea'">
      <q-input
        ref="input"
        type="textarea"
        autogrow
        v-model="currentValue"
        :placeholder="placeholder"
        :bg-color="bgColor"
        :disable="disabled"
        :counter="enableCounter"
        :maxlength="maxLength"
        bottom-slots
        lazy-rules
        :rules="[...rules, validateRequired, validateMinLength, validateMaxLength]"
        hide-bottom-space
        outlined
        dense
      >
        <template v-slot:error>
          {{ errorMsg }}
        </template>
      </q-input>
    </template>

    <!-- Password -->
    <template v-else-if="type === 'password'">
      <q-input
        ref="input"
        type="password"
        v-model="currentValue"
        :placeholder="placeholder"
        :bg-color="bgColor"
        :disable="disabled"
        :counter="enableCounter"
        :maxlength="maxLength"
        bottom-slots
        lazy-rules
        :rules="[...rules, validateRequired, validateMinLength, validateMaxLength]"
        hide-bottom-space
        outlined
        dense
      >
        <template v-slot:append>
          <slot name="append" />
        </template>
      </q-input>
    </template>

    <!-- File -->
    <template v-else-if="type === 'file'">
      <q-file
        ref="input"
        v-model="currentValue"
        :bg-color="bgColor"
        :disable="disabled"
        bottom-slots
        lazy-rules
        :rules="[...rules, validateRequired]"
        hide-bottom-space
        outlined
        dense
      >
        <template v-slot:prepend>
          <q-icon name="attach_file" @click="pickFiles" />
          <p
            class="text-body2 text-secondary q-ma-none q-ml-sm cursor-pointer"
            v-if="!currentValue"
            @click="pickFiles"
          >
            Choose a file
          </p>
        </template>
      </q-file>
    </template>

    <!-- Basic -->
    <template v-else>
      <q-input
        ref="input"
        v-model="currentValue"
        :placeholder="placeholder"
        :bg-color="bgColor"
        :disable="disabled"
        :counter="enableCounter"
        :maxlength="maxLength"
        bottom-slots
        lazy-rules
        :rules="[...rules, validateRequired, validateMinLength, validateMaxLength]"
        hide-bottom-space
        outlined
        dense
      >
        <template v-slot:prepend>
          <slot name="prepend" />
        </template>
        <template v-slot:append>
          <slot name="append" />
        </template>
      </q-input>
    </template>
  </div>
</template>

<script>
const messages = {
  en: {
    '{name} is required.': '{name} is required.',
    'Please enter a number smaller than {number}.': 'Please enter a number smaller than {number}.',
    'Please enter a number greater than {number}.': 'Please enter a number greater than {number}.',
    'Please enter within {number} characters.': 'Please enter within {number} characters.',
    'Please enter at least {number} characters.': 'Please enter at least {number} characters.',
  },
  ja: {
    '{name} is required.': '{name}を入力してください。',
    'Please enter a number smaller than {number}.': '{number}以下の数字を入力してください。',
    'Please enter a number greater than {number}.': '{number}以上の数字を入力してください。',
    'Please enter within {number} characters.': '{number}文字以内で入力してください。',
    'Please enter at least {number} characters.': '{number}文字以上で入力してください。',
  },
};

export default {
  name: 'Input',
  i18n: { messages },
  props: {
    value: {
      type: [String, Number],
    },
    type: {
      type: String,
      default: 'text',
    },
    name: {
      type: String,
    },
    label: {
      type: String,
      default: '',
    },
    description: {
      type: String,
    },
    placeholder: {
      type: String,
    },
    bgColor: {
      type: String,
      default: 'white',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    prefix: {
      type: String,
    },
    suffix: {
      type: String,
    },
    width: {
      type: String,
      default: '100%',
    },
    maxWidth: {
      type: String,
    },

    // Validate
    required: {
      type: Boolean,
      default: false,
    },
    min: {
      type: Number,
    },
    max: {
      type: Number,
    },
    minLength: {
      type: Number,
    },
    maxLength: {
      type: Number,
    },
    rules: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    currentValue: {
      set(value) {
        this.$emit('input', value);
      },
      get() {
        return this.value;
      },
    },

    enableCounter() {
      return !!this.minLength || !!this.maxLength;
    },
  },
  methods: {
    validateRequired(val) {
      return this.required && !val && val !== 0
        ? this.$t('{name} is required.', { name: this.label || this.name })
        : null;
    },

    validateMax(val) {
      return typeof this.max === 'number' && val > this.max
        ? this.$t('Please enter a number smaller than {number}.', { number: this.max })
        : null;
    },

    validateMin(val) {
      return typeof this.min === 'number' && val < this.min
        ? this.$t('Please enter a number greater than {number}.', { number: this.min })
        : null;
    },

    validateMaxLength(val) {
      return typeof this.maxLength === 'number' && val.length > this.maxLength
        ? this.$t('Please enter within {number} characters.', { number: this.maxLength })
        : null;
    },

    validateMinLength(val) {
      return typeof this.minLength === 'number' && val.length < this.minLength
        ? this.$t('Please enter at least {number} characters.', { number: this.minLength })
        : null;
    },

    handleChange() {
      this.$emit('change', this.currentValue);
    },

    resetValidation() {
      this.$refs.input.resetValidation();
    },

    pickFiles() {
      this.$refs.input.pickFiles();
    },
  },
};
</script>

<style lang="scss" scoped></style>
